import React, {FC} from "react";
import {LinkOutlined} from "@ant-design/icons";
import {Button, Form, Select, Space, Typography} from "antd";
import {useTranslation} from "react-i18next";
import {FieldTile} from "../../../Components/ui";
import {DetailPanel, FIELD_GRID_STYLE, useEnumLabel} from "./participantDetailUi";
import type {ParticipantDetailModel} from "./useParticipantDetail";

// The payer of the primary registration. It is chosen when the participant registers and can be changed
// with an explicit action until a participation offer was issued to it.
export const PayerSection: FC<{model: ParticipantDetailModel}> = ({model}) => {
    const {t} = useTranslation();
    const enumLabel = useEnumLabel();
    const {payer, primaryRegistration, pending, actions, forms} = model;
    const details = payer.selected;
    const canEdit = !!primaryRegistration && !payer.isLocked;

    return (
        <DetailPanel
            title={t("payer", {defaultValue: "Payer"})}
            extra={(
                <Space wrap>
                    <Button
                        icon={<LinkOutlined/>}
                        href={primaryRegistration?.payerId ? `/Admin/Payers/${primaryRegistration.payerId}` : undefined}
                        disabled={!primaryRegistration?.payerId}
                    >
                        {t("open-payer", {defaultValue: "Open payer"})}
                    </Button>
                    <Button href="/Admin/Payers">
                        {t("new-payer", {defaultValue: "New payer"})}
                    </Button>
                    <Button
                        type="primary"
                        loading={pending.changingPayer}
                        disabled={!canEdit || !payer.isChanged}
                        onClick={actions.changePayer}
                    >
                        {t("change-payer", {defaultValue: "Change payer"})}
                    </Button>
                </Space>
            )}
        >
            {!primaryRegistration ? (
                <Typography.Paragraph type="secondary">
                    {t("participant-payer-chosen-at-registration", {
                        defaultValue: "The payer is chosen when the participant is registered."
                    })}
                </Typography.Paragraph>
            ) : null}
            {primaryRegistration && payer.isLocked ? (
                <Typography.Paragraph type="secondary">
                    {t("participant-payer-locked", {
                        defaultValue: "An offer was already issued to this payer, so the payer cannot be changed."
                    })}
                </Typography.Paragraph>
            ) : null}
            <Form form={forms.payer}>
                <div style={FIELD_GRID_STYLE}>
                    <div data-testid="participant-payer-selector-field" style={{gridColumn: "1 / -1"}}>
                        <FieldTile label={t("select-payer", {defaultValue: "Select payer"})}>
                            <Form.Item name="payerId" noStyle>
                                <Select
                                    showSearch
                                    filterOption={false}
                                    disabled={!canEdit}
                                    loading={payer.loading}
                                    aria-label={t("select-payer", {defaultValue: "Select payer"})}
                                    placeholder={t("select-payer-placeholder", {
                                        defaultValue: "Search by name, email, or tax ID"
                                    })}
                                    style={{width: "100%"}}
                                    onSearch={payer.setSearch}
                                    onChange={payer.select}
                                    options={payer.options.map(option => ({
                                        value: option.id,
                                        label: [
                                            option.name?.trim() || `#${option.id}`,
                                            option.email?.trim(),
                                            option.taxId?.trim()
                                        ].filter(Boolean).join(" · ")
                                    }))}
                                />
                            </Form.Item>
                        </FieldTile>
                    </div>
                    <FieldTile label={t("payer-type", {defaultValue: "Payer type"})}>
                        {enumLabel(details?.type ?? payer.values.payerType)}
                    </FieldTile>
                    <FieldTile label={t("payer-email", {defaultValue: "Payer email"})}>
                        {details?.email ?? payer.values.payerEmail}
                    </FieldTile>
                    <FieldTile label={t("payer-phone", {defaultValue: "Payer phone"})}>
                        {details?.phone ?? payer.values.payerPhone}
                    </FieldTile>
                    <FieldTile label={t("payer-address", {defaultValue: "Payer address"})}>
                        {details?.address ?? payer.values.payerAddress}
                    </FieldTile>
                    <FieldTile label={t("payer-city", {defaultValue: "Payer city"})}>
                        {details?.city ?? payer.values.payerCity}
                    </FieldTile>
                    <FieldTile label={t("payer-tax-id", {defaultValue: "Payer OIB"})}>
                        {details?.taxId ?? payer.values.payerTaxId}
                    </FieldTile>
                </div>
            </Form>
        </DetailPanel>
    );
};
